import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, SelectChangeEvent, Typography } from '@mui/material';
import { BUSINESS_PLAN_STATUS } from 'constants/businessPlan';
import { isEmpty } from 'lodash';
import { BusinessPlanApi } from 'services/businessPlan/businessPlan.api';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { PLAN_ACCESS_TYPE } from 'services/businessPlan/types';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IBusinessPlanStatus } from 'interfaces/businessPlan';

import { BlackToast } from 'components/_basic/BlackToast/BlackToast';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { Loader } from 'components/_basic/Loader/Loader';
import { Modal } from 'components/_basic/Modal';
import { ChangeDocAccessSelect } from 'components/ChangeDocAccessSelect/ChangeDocAccessSelect';
import { IDropdownOptions } from 'components/ChangeDocAccessSelect/types';
import { CopyLinkButton } from 'components/CopyLinkButton/CopyLinkButton';
import { DataPrivacyModal } from 'components/DataPrivacyModal';
import { ShareLinksForm } from 'components/ShareLinksForm/ShareLinksForm';
import { IAccountPersonalInfoForm } from 'components/ShareLinksForm/types';

import { getValidatedMetadata } from 'helpers/metadataParser';
import ToastService from 'helpers/ToastService';
import { grantAccessValidationSchema } from 'helpers/validations/grantAccessValidation';
import { copyPdfLink } from 'helpers/viewDocHandler';

import { IShareProjectModalProps as IProps } from './types';

import styles from './ShareProjectModal.module.scss';

export const ShareProjectModal: FC<IProps> = ({ onClose, id }) => {
  const { t } = useTranslation();
  const [isDataPrivacyModalOpen, setIsDataPrivacyModalOpen] = useState(false);
  const [isUserAcceptedPrivacy, setIsUserAcceptedPrivacy] = useState(false);

  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [dropdownValue, setDropdownValue] = useState<IBusinessPlanStatus>(BUSINESS_PLAN_STATUS.PUBLISHED);

  const { track, isPending } = usePromiseStatus();
  const updateBusinessPlan = useUpdateBusinessPlan();
  const businessPlan = useBusinessToolStore(getBusinessPlan);

  useEffect(() => {
    if (businessPlan) {
      setDropdownValue(businessPlan.status);
    }
  }, [businessPlan]);

  const isRestricted = dropdownValue === BUSINESS_PLAN_STATUS.DRAFT;

  const dropdownOptions: IDropdownOptions[] = [
    {
      value: BUSINESS_PLAN_STATUS.DRAFT,
      title: t('restrictedAccess'),
      text: t('onlyYouCanAccess'),
    },
    {
      value: BUSINESS_PLAN_STATUS.PUBLISHED,
      title: t('anyoneWithTheLink'),
      text: t('anyoneCanView'),
    },
  ];

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
    reset,
  } = useForm<IAccountPersonalInfoForm>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver<IAccountPersonalInfoForm>(grantAccessValidationSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const onSubmit = async ({ email }: IAccountPersonalInfoForm) => {
    try {
      await track(
        BusinessPlanApi.grantEditAccess({
          id,
          body: {
            email,
            type: PLAN_ACCESS_TYPE.VIEW,
          },
        }),
      );

      toast(<BlackToast text="Viewer has been invited" />, {
        position: 'top-center',
      });

      reset();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  const copyLinkHandler = () => {
    copyPdfLink(id);
    setIsLinkCopied(true);
  };

  const dropdownChangeHandler = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as IBusinessPlanStatus;

    setDropdownValue(value);

    try {
      await track(
        updateBusinessPlan.mutateAsync({
          id,
          body: {
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
            }),
            status: value,
          },
        }),
      );
    } catch (error) {
      error instanceof Error && ToastService.showError(error.message);
    }
  };

  const onCloseDataPrivacyModal = () => {
    setIsUserAcceptedPrivacy(true);
    setIsDataPrivacyModalOpen(false);
  };

  if (isEmpty(businessPlan)) return <Loader />;

  return (
    <>
      {!isDataPrivacyModalOpen && (
        <Modal close={onClose} showCloseButton={false}>
          <Box className={styles.wrapper}>
            <Grid className={styles.header} container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography component="h3" variant="h3">
                  {t('shareProject')}
                </Typography>
              </Grid>
              <Grid item>
                <CloseButton onClick={onClose} />
              </Grid>
            </Grid>
            {!isRestricted && (
              <ShareLinksForm
                register={register}
                buttonText={t('share')}
                label={t('inviteViewers')}
                endAdornmentText={t('canView')}
                error={errors.email?.message}
                isButtonDisabled={!isValid || isPending}
                isLoading={isPending}
                onSubmit={
                  isUserAcceptedPrivacy
                    ? handleSubmit(onSubmit)
                    : () => {
                        setIsDataPrivacyModalOpen(true);
                      }
                }
              />
            )}
            <Box className={styles.controlWrapper}>
              <ChangeDocAccessSelect
                value={dropdownValue}
                options={dropdownOptions}
                isRestricted={isRestricted}
                onChange={dropdownChangeHandler}
              />
              <CopyLinkButton onClick={copyLinkHandler} isLinkCopied={isLinkCopied} disabled={isRestricted} />
            </Box>
          </Box>
        </Modal>
      )}

      <DataPrivacyModal onClose={onCloseDataPrivacyModal} open={isDataPrivacyModalOpen} />
    </>
  );
};
